import Vue from "vue";
import VueI18n from "vue-i18n";
import Const from "@/utils/consts";
import zh_hans from "@/locales/zh-hans";
import zh_hant from "@/locales/zh-hant";
import en_Us from "@/locales/en-us";
import ja_Jp from "@/locales/ja-jp";
import ko_Kr from "@/locales/ko-kr";
import en_Sg from "@/locales/en-sg";
import en_Gb from "@/locales/en-gb";
import de_De from "@/locales/de-de";
import fr_FR from "@/locales/fr-fr";
import ar_AE from "@/locales/ar-ae";

// 语言库
const messages = {
  "zh-hans": zh_hans,
  "zh-hant": zh_hant,
  "en-us": en_Us,
  "ja-jp": ja_Jp,
  "ko-kr": ko_Kr,
  "en-sg": en_Sg,
  "en-gb": en_Gb,
  "de-de": de_De,
  "fr-fr": fr_FR,
  "ar-ae": ar_AE,
};

Vue.use(VueI18n);

const defLang = { text: "English", lang: "en-us" };

export const SUPPORTED_LANGUAGES = {
  "zh-hans": { text: "简体中文", lang: "zh-hans" },
  "zh-hant": { text: "繁體中文", lang: "zh-hant" },
  "en-us": { text: "English (US)", lang: "en-us" },
  "en-gb": { text: "English (GB)", lang: "en-gb" },
  "ja-jp": { text: "日本語", lang: "ja-jp" },
  "ko-kr": { text: "한국어", lang: "ko-kr" },
  "en-sg": { text: "English (SG)", lang: "en-sg" },
  "de-de": { text: "Deutsch", lang: "de-de" },
  "fr-fr": { text: "Français", lang: "fr-fr" },
  "ar-ae": { text: "العربية", lang: "ar-ae" },
};

const i18n = new VueI18n({
  fallbackLocale: "en-us",
  locale: "en-us",
  messages: messages,
  silentFallbackWarn: true,
});

// ========== 核心：记录每个语言加载了哪些模块 ==========
const loadedModules = {
  // 'zh-hans': ['base', 'product-a'],
  // 'en-us': ['base', 'product-a'],
};

// 当前语言
let currentLang = "en-us";

/**
 * 加载单个模块
 */
export async function loadModule(langKey, moduleName, options = {}) {
  const {
    basePath = "/statics/locales",
    path = "",
    module = "",
    useCache = false,
  } = options;

  // 检查该语言是否已加载该模块
  if (loadedModules[langKey]?.includes(moduleName)) {
    // console.log(`⏭️ [${langKey}] 模块已加载: ${moduleName}`);
    return;
  }

  try {
    let url = `${basePath}/${langKey}/${moduleName}.json`;
    if (path) {
      url = `${basePath}/${langKey}/${path}${moduleName}.json`;
    }
    if (!useCache) {
      url = `${url}?v=${Date.now()}`;
    }

    // console.log(`📥 [${langKey}] 加载模块: ${moduleName}`);

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const translations = await response.json();

    // 合并到现有翻译（不覆盖其他模块）
    const currentMessages = i18n.getLocaleMessage(langKey) || {};
    let merged = { ...currentMessages, ...translations };
    if (module) {
      currentMessages[module] = translations;
      merged = currentMessages;
    }

    i18n.setLocaleMessage(langKey, merged);

    // 记录已加载的模块
    if (!loadedModules[langKey]) {
      loadedModules[langKey] = [];
    }
    loadedModules[langKey].push(moduleName);

    // console.log(
    //   `✅ [${langKey}] 模块加载成功: ${moduleName} (${
    //     Object.keys(translations).length
    //   } 条)`,
    // );
  } catch (error) {
    console.error(`❌ [${langKey}] 模块加载失败: ${moduleName}`, error);
    // 不抛出错误，允许继续加载其他模块
  }
}

/**
 * 加载基础语言包
 */
export async function loadBaseLanguage(langKey, options = {}) {
  return loadModule(langKey, "index", options);
}

/**
 * 加载产品专属翻译
 */
export async function loadProductLanguage(langKey, productId, options = {}) {
  return loadModule(langKey, `${productId}`, options);
}

/**
 * 批量加载产品翻译
 * @param {string[]} productIds - 产品 ID 数组，如 ['AI800001', 'AI800002']
 */
export async function loadProductsLanguage(productIds, options = {}) {
  const lang = currentLang;
  const promises = productIds.map((id) =>
    loadModule(lang, id, { ...options, module: id }).catch(() => {
      console.warn(`⚠️ 产品 ${id} 翻译加载失败`);
    }),
  );
  await Promise.all(promises);
}

/**
 * 批量加载模块
 */
export async function loadModules(langKey, moduleNames, options = {}) {
  const langModules = loadedModules[langKey] || [];
  const needLoad = moduleNames.filter((name) => !langModules.includes(name));

  if (needLoad.length === 0) {
    // console.log(`⏭️ [${langKey}] 所有模块已加载: ${moduleNames.join(", ")}`);
    return;
  }

  const promises = needLoad.map((name) => loadModule(langKey, name, options));
  await Promise.all(promises);
}

/**
 * 切换语言
 */
export async function switchLanguage(langKey, options = {}) {
  if (!SUPPORTED_LANGUAGES[langKey]) {
    console.warn(`⚠️ 不支持的语言: ${langKey}`);
    return;
  }

  console.log(`🔄 切换语言: ${currentLang} → ${langKey}`);

  // 1. 切换 locale（VueI18n 会自动使用该语言的翻译）
  i18n.locale = langKey;
  currentLang = langKey;

  // 2. 检查该语言是否已加载过
  const hasLoaded = loadedModules[langKey] && loadedModules[langKey].length > 0;

  if (!hasLoaded) {
    // 首次加载该语言：加载 base
    console.log(`📥 [${langKey}] 首次加载，加载基础模块`);
    await loadBaseLanguage(langKey, options);
  } else {
    // 已加载过：直接使用缓存，但确保所有模块都存在
    // console.log(
    //   `✅ [${langKey}] 使用已缓存的翻译 (${loadedModules[langKey].join(", ")})`,
    // );
    // 可选：重新验证所有模块是否完整
    // 如果某些模块缺失，可以重新加载
  }

  console.log(`✅ 语言切换成功: ${langKey}`);
}

/**
 * 设置语言（对外接口）
 */
export const setLanguage = async (lang) => {
  if (!lang) {
    const cached = localStorage.getItem(Const.KEY_LANG);
    if (cached) {
      try {
        lang = JSON.parse(cached);
      } catch (e) {
        lang = defLang;
      }
    } else {
      lang = defLang;
    }
  }

  if (!SUPPORTED_LANGUAGES[lang.lang]) {
    lang = defLang;
  }

  console.log("lang", lang);
  localStorage.setItem(Const.KEY_LANG, JSON.stringify(lang));

  // 更新 DOM
  Object.keys(SUPPORTED_LANGUAGES).forEach((langKey) => {
    document.body.classList.remove(`lang-${langKey}`);
  });
  document.body.classList.add(`lang-${lang.lang}`);
  // document.body.setAttribute("lang", lang.lang);

  // 切换语言
  await switchLanguage(lang.lang);
};

/**
 * 获取当前语言
 */
export const getCurrentLanguage = () => {
  return i18n.locale;
};

/**
 * 获取已加载的模块列表
 */
export const getLoadedModules = (langKey) => {
  return loadedModules[langKey] || [];
};

/**
 * 检查模块是否已加载
 */
export const isModuleLoaded = (langKey, moduleName) => {
  return loadedModules[langKey]?.includes(moduleName) || false;
};

// ========== 初始化 ==========
const initLanguage = async () => {
  try {
    const cached = localStorage.getItem(Const.KEY_LANG);
    let lang = defLang;
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (SUPPORTED_LANGUAGES[parsed.lang]) {
          lang = parsed;
        }
      } catch (e) {
        console.warn("⚠️ 无效的语言配置:", e);
      }
    }
    await setLanguage(lang);
  } catch (error) {
    console.error("初始化语言失败:", error);
    await setLanguage(defLang);
  }
};

initLanguage();

export default i18n;
